import {
  effectiveAivorRecordId,
  equivalentUnitKey,
  isClaimedAivorOnlyRow,
  matchSageUnit,
  parseSageSnapshot,
  SageDecision,
  SageEntityTypeName,
  SageReconciliationMatch,
  sageSnapshotName,
  UnitOfMeasureOption,
} from './sage-integration.model';
import { suggestUnitFromName, UnitSuggestion } from './sage-unit-suggest.model';

export interface WizardStepCopy {
  noun: string;
  nouns: string;
  why: string;
}

export const WIZARD_STEP_COPY: Record<SageEntityTypeName, WizardStepCopy> = {
  SupplierCategory: {
    noun: 'supplier category',
    nouns: 'supplier categories',
    why: 'Groups like "Direct Materials". We set these up first, because suppliers use them.',
  },
  Supplier: {
    noun: 'supplier',
    nouns: 'suppliers',
    why: 'The companies you buy from. We check each Sage supplier against the ones in Aivor.',
  },
  ItemCategory: {
    noun: 'item category',
    nouns: 'item categories',
    why: 'Groups for your stock items, like "Raw Materials".',
  },
  Item: {
    noun: 'item',
    nouns: 'items',
    why: 'Your raw materials, finished goods and services, with their unit of measure.',
  },
  ItemBundle: {
    noun: 'bundle',
    nouns: 'bundles',
    why: 'Products made of other items. They need their items linked first.',
  },
};

export type WizardExceptionKind = 'pair' | 'new-to-aivor' | 'missing-aivor' | 'unit';

export interface WizardException {
  kind: WizardExceptionKind;
  matchId: number;
}

export interface StepExceptionContext {
  entityType: SageEntityTypeName;
  /** Null until the Aivor records load: a record can't be called missing before then. */
  aivorRecordIds: ReadonlySet<number> | null;
  /** Items only; null until the units load. */
  units: UnitOfMeasureOption[] | null;
  /** Equivalent units whose save failed, so confirm still wouldn't match them. */
  unsavedUnitIds?: ReadonlySet<number>;
}

const OPEN_DECISIONS: SageDecision[] = ['Pending', 'Changed', 'Skipped'];

export function sageUnitOf(match: SageReconciliationMatch): string {
  const raw = parseSageSnapshot(match.sageRecordSnapshot);
  return (raw?.Unit ?? raw?.unit ?? '').trim();
}

function needsUnit(match: SageReconciliationMatch, ctx: StepExceptionContext): boolean {
  if (ctx.entityType !== 'Item') return false;
  if (match.decision === 'NeedsUomMapping') return true;
  if (match.decision !== 'ImportToAivor' || match.resolvedUOMID || !ctx.units) return false;
  if (ctx.unsavedUnitIds?.has(match.id)) return true;
  return !matchSageUnit(sageUnitOf(match), ctx.units);
}

/** A unit the user picked because Sage's didn't match. Its question stays on screen, answered, when
 * they come back, instead of moving to "Units matched automatically". */
export function unitChosenByHand(match: SageReconciliationMatch, ctx: StepExceptionContext): boolean {
  return (
    ctx.entityType === 'Item' &&
    match.decision === 'ImportToAivor' &&
    !!match.resolvedUOMID &&
    !!ctx.units &&
    !matchSageUnit(sageUnitOf(match), ctx.units)
  );
}

/** The cards a step shows. A match can give two: an item that turns out to be new still needs a unit.
 * Aivor-only rows never ask: they are sent to Sage, or are the other half of a pair already asked. */
export function exceptionKindsFor(match: SageReconciliationMatch, ctx: StepExceptionContext): WizardExceptionKind[] {
  if (!match.sageRecordId) return [];
  const kinds: WizardExceptionKind[] = [];
  const aivorId = effectiveAivorRecordId(match);
  if (OPEN_DECISIONS.includes(match.decision)) {
    kinds.push(aivorId ? 'pair' : 'new-to-aivor');
  } else if (match.decision === 'Confirmed' && aivorId && ctx.aivorRecordIds && !ctx.aivorRecordIds.has(aivorId)) {
    kinds.push('missing-aivor');
  }
  if (needsUnit(match, ctx) || unitChosenByHand(match, ctx)) kinds.push('unit');
  return kinds;
}

export function stepExceptions(matches: SageReconciliationMatch[], ctx: StepExceptionContext): WizardException[] {
  return matches.flatMap((m) => exceptionKindsFor(m, ctx).map((kind) => ({ kind, matchId: m.id })));
}

/** Answered cards stay on screen so the answer can change; new answers can reveal more. */
export function mergeExceptions(shown: WizardException[], latest: WizardException[]): WizardException[] {
  const key = (e: WizardException) => `${e.kind}:${e.matchId}`;
  const seen = new Set(shown.map(key));
  return [...shown, ...latest.filter((e) => !seen.has(key(e)))];
}

export function isExceptionAnswered(
  exception: WizardException,
  match: SageReconciliationMatch | undefined,
  ctx: StepExceptionContext
): boolean {
  if (!match) return true;
  switch (exception.kind) {
    case 'pair':
    case 'new-to-aivor':
      return !OPEN_DECISIONS.includes(match.decision);
    case 'missing-aivor': {
      if (match.decision === 'ImportToAivor') return true;
      const aivorId = effectiveAivorRecordId(match);
      return !!aivorId && !!ctx.aivorRecordIds?.has(aivorId);
    }
    case 'unit':
      return (match.decision !== 'ImportToAivor' && match.decision !== 'NeedsUomMapping') || !!match.resolvedUOMID;
  }
}

export type UnitGroupKind = 'suggested' | 'sage-unit' | 'none';

export interface UnitGroup {
  key: string;
  kind: UnitGroupKind;
  /** The unit read from the names, for a suggested group. */
  unit: UnitOfMeasureOption | null;
  sageUnit: string;
  matches: SageReconciliationMatch[];
  suggestions: ReadonlyMap<number, UnitSuggestion>;
}

export function unitGroups(matches: SageReconciliationMatch[], units: UnitOfMeasureOption[]): UnitGroup[] {
  const groups = new Map<string, UnitGroup>();
  const add = (key: string, init: () => Omit<UnitGroup, 'matches' | 'suggestions'>, match: SageReconciliationMatch, suggestion?: UnitSuggestion) => {
    let group = groups.get(key);
    if (!group) {
      group = { ...init(), matches: [], suggestions: new Map() };
      groups.set(key, group);
    }
    group.matches.push(match);
    if (suggestion) (group.suggestions as Map<number, UnitSuggestion>).set(match.id, suggestion);
  };
  for (const match of matches) {
    const sageUnit = sageUnitOf(match);
    if (sageUnit) {
      // Sage's own unit is a better clue than the name, so the name isn't read.
      const key = `sage-${encodeURIComponent(equivalentUnitKey(sageUnit))}`;
      add(key, () => ({ key, kind: 'sage-unit', unit: null, sageUnit }), match);
      continue;
    }
    const suggestion = suggestUnitFromName(sageSnapshotName(match.sageRecordSnapshot, 'Item'), units);
    if (suggestion) {
      const key = `suggested-${suggestion.unit.id}`;
      add(key, () => ({ key, kind: 'suggested', unit: suggestion.unit, sageUnit: '' }), match, suggestion);
    } else {
      add('none', () => ({ key: 'none', kind: 'none', unit: null, sageUnit: '' }), match);
    }
  }
  const rank = (g: UnitGroup) => (g.kind === 'suggested' ? 0 : 1);
  return [...groups.values()].sort((a, b) => rank(a) - rank(b) || b.matches.length - a.matches.length);
}

/** The unit more than half the group has. A plain "most common" would let two items changed one by
 * one become the group's unit, and the next group choice would then overwrite them. */
export function groupUnit(members: SageReconciliationMatch[]): number | null {
  const counts = new Map<number, number>();
  for (const m of members) if (m.resolvedUOMID) counts.set(m.resolvedUOMID, (counts.get(m.resolvedUOMID) ?? 0) + 1);
  for (const [id, n] of counts) if (n * 2 > members.length) return id;
  return null;
}

/** Items given their own unit keep it when the group's unit changes. */
export function groupTargets(members: SageReconciliationMatch[], previous: number | null): SageReconciliationMatch[] {
  return members.filter((m) => !m.resolvedUOMID || (previous != null && m.resolvedUOMID === previous));
}

/** "Needs your help": a unit group counts once, however many items it holds. */
export function openQuestionCount(matches: SageReconciliationMatch[], ctx: StepExceptionContext): number {
  const byId = new Map(matches.map((m) => [m.id, m]));
  const exceptions = stepExceptions(matches, ctx);
  const isOpen = (e: WizardException) => !isExceptionAnswered(e, byId.get(e.matchId), ctx);
  const cards = exceptions.filter((e) => e.kind !== 'unit' && isOpen(e)).length;
  const unitMatches = exceptions.filter((e) => e.kind === 'unit').map((e) => byId.get(e.matchId)!);
  const groups = unitGroups(unitMatches, ctx.units ?? []).filter((g) =>
    g.matches.some((m) => isOpen({ kind: 'unit', matchId: m.id }))
  );
  return cards + groups.length;
}

export interface DecisionCounts {
  linked: number;
  toAivor: number;
  toSage: number;
  total: number;
}

export function decisionCounts(matches: SageReconciliationMatch[]): DecisionCounts {
  const rows = matches.map((match) => ({ match }));
  const counted = rows.filter((r) => !isClaimedAivorOnlyRow(r, rows)).map((r) => r.match);
  return {
    linked: counted.filter((m) => m.decision === 'Confirmed' && !!m.sageRecordId && !!effectiveAivorRecordId(m)).length,
    toAivor: counted.filter((m) => m.decision === 'ImportToAivor' || m.decision === 'NeedsUomMapping').length,
    toSage: counted.filter((m) => m.decision === 'CreateInSage').length,
    total: counted.length,
  };
}

export function sumDecisionCounts(all: DecisionCounts[]): DecisionCounts {
  return all.reduce(
    (sum, c) => ({
      linked: sum.linked + c.linked,
      toAivor: sum.toAivor + c.toAivor,
      toSage: sum.toSage + c.toSage,
      total: sum.total + c.total,
    }),
    { linked: 0, toAivor: 0, toSage: 0, total: 0 }
  );
}

// Shared by the step's chips and its summary sentence, so the two can't word it differently.
export function directionLabels(counts: DecisionCounts): string[] {
  const labels: string[] = [];
  if (counts.toAivor) labels.push(`${counts.toAivor} will be added to Aivor`);
  if (counts.toSage) labels.push(`${counts.toSage} will be sent to Sage`);
  return labels;
}

function joinAnd(parts: string[]): string {
  return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function stepSummary(copy: WizardStepCopy, counts: DecisionCounts, open: number): string {
  if (counts.total === 0) return `No ${copy.nouns} in Sage or Aivor yet, so there is nothing to match.`;
  const parts: string[] = [];
  if (counts.linked) parts.push(`${counts.linked} matched`);
  parts.push(...directionLabels(counts));
  if (open) parts.push(`${open} need${open === 1 ? 's' : ''} your help`);
  const noun = counts.total === 1 ? copy.noun : copy.nouns;
  return parts.length ? `${counts.total} ${noun} compared. ${joinAnd(parts)}.` : `${counts.total} ${noun} compared.`;
}

export type WizardStepState = 'sent' | 'needs' | 'done' | 'todo';

export function stepState(confirmed: boolean, open: number, passed: boolean): WizardStepState {
  if (confirmed) return 'sent';
  if (open > 0) return 'needs';
  return passed ? 'done' : 'todo';
}

/** Index of the first step still to do, or -1 when every step is ready for the review. */
export function resumeIndex(states: WizardStepState[]): number {
  return states.findIndex((s) => s === 'needs' || s === 'todo');
}

/** Component items of a bundle that won't be in Sage once the Items step has been sent. */
export function bundleComponentsNotReady(
  componentItemIds: number[],
  itemMatches: SageReconciliationMatch[],
  aivorItems: any[]
): number[] {
  return componentItemIds.filter((itemId) => {
    const item = aivorItems.find((i) => (i.itemID ?? i.ItemID) === itemId);
    if (item?.sageItemId ?? item?.SageItemId) return false;
    return !itemMatches.some(
      (m) =>
        (m.decision === 'Confirmed' && !!m.sageRecordId && effectiveAivorRecordId(m) === itemId) ||
        (m.decision === 'CreateInSage' && m.aivorRecordId === itemId)
    );
  });
}

function sameUnitText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function unitMatchLabel(sageUnit: string, option: UnitOfMeasureOption): string {
  if (sameUnitText(sageUnit, option.name)) return option.name;
  if (sameUnitText(sageUnit, option.code)) return `${sageUnit} → ${option.name}`;
  return `${sageUnit} → ${option.code} ${option.name}`;
}

const PASSED_STEPS_KEY = 'sageWizard.passed.';

/** Step slug → the session id the user pressed Next on. A new session for that step resets it. */
export function readPassedSteps(tenantKey: string): Record<string, number> {
  try {
    const parsed = JSON.parse(localStorage.getItem(PASSED_STEPS_KEY + tenantKey) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writePassedStep(tenantKey: string, slug: string, sessionId: number): void {
  try {
    localStorage.setItem(PASSED_STEPS_KEY + tenantKey, JSON.stringify({ ...readPassedSteps(tenantKey), [slug]: sessionId }));
  } catch {
    // Storage blocked: the step just shows as not started again, its answers are saved server-side.
  }
}

export function clearPassedSteps(tenantKey: string): void {
  try {
    localStorage.removeItem(PASSED_STEPS_KEY + tenantKey);
  } catch {
    // Nothing to clear.
  }
}
